import { usd, fmtNum } from '../../lib/q2c';

const perSf = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);

/**
 * 4. Pricing basis (LP-402, internal): the comparables the price was anchored on, the floor, where each
 * cost came from, and the builder's assumptions and open questions. basis: the quote's stored
 * pricing_basis; view: the builder view (the floor and each line's cost sources, for the form as it is).
 */
export default function BasisPanel({ basis, view }) {
  const comps = basis?.comparables || [];
  const fl = view?.totals?.floor;
  const area = view?.totals?.area_sf;
  const sources = (view?.lines || []).filter((r) => !r.optional || r.expected.unit != null);
  return (
    <details open className="group rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
      <summary className="cursor-pointer list-none">
        <h2 className="inline text-sm font-semibold">4. Pricing basis</h2>
        <span className="ml-1 text-xs text-[var(--text-secondary)] group-open:hidden">(show)</span>
        <span className="ml-1 hidden text-xs text-[var(--text-secondary)] group-open:inline">(hide)</span>
        <p className="text-xs text-[var(--text-secondary)]">{comps.length ? `Priced like ${comps.filter((c) => c.named).concat(comps.filter((c) => !c.named)).slice(0, 2).map((c) => c.customer || c.title || c.number).join(' and ')}. ` : ''}{view?.floor_rule}</p>
      </summary>
      <div className="mt-3 space-y-4 text-sm">
        <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
          <table className="w-full min-w-[40rem]">
            <thead className="bg-gray-50 text-left text-xs dark:bg-slate-800"><tr><th className="p-2">Comparable</th><th className="p-2">Area</th><th className="p-2 text-right">Per sf, all-in</th><th className="p-2">Note</th></tr></thead>
            <tbody className="align-top">
              {comps.map((c) => (
                <tr key={c.number} className="border-t border-[var(--border)]">
                  <td className="p-2"><p className="font-medium">{c.named ? '★ ' : ''}{c.customer || c.title} {c.number}</p><p className="text-xs text-[var(--text-secondary)]">{c.status}{c.date ? `, ${c.date}` : ''}</p></td>
                  <td className="p-2 tabular-nums">{fmtNum(c.area_sf)} sf</td>
                  <td className="p-2 text-right tabular-nums">{perSf(c.all_in_per_sf)}</td>
                  <td className="p-2 text-xs">{c.shake_per_sf != null ? `Dry shake ${perSf(c.shake_per_sf)}/sf` : ''}{c.all_in_per_lb != null ? `, $${Number(c.all_in_per_lb).toFixed(2)}/lb all-in` : ''}</td>
                </tr>
              ))}
              <tr className="border-t border-[var(--border)] bg-gray-50 dark:bg-slate-800/40">
                <td className="p-2 font-medium">This quote</td>
                <td className="p-2 tabular-nums">{area ? `${fmtNum(area)} sf` : '—'}</td>
                <td className="p-2 text-right tabular-nums">{perSf(view?.totals?.per_sf)}</td>
                <td className="p-2 text-xs">{fl ? <>Floor at {view.basis} cost (falling back where unknown): {usd(fl.total)}{fl.per_sf != null ? ` (${perSf(fl.per_sf)} per sf)` : ''}. {fl.above ? 'We are above it.' : <b>We are below it.</b>}</> : 'No cost known yet, so no floor.'}</td>
              </tr>
              {!comps.length && <tr className="border-t border-[var(--border)]"><td colSpan={4} className="p-2 text-xs text-[var(--text-secondary)]">No past quote to compare with.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <p className="mb-1 font-medium">Where the costs come from</p>
            <ul className="list-disc space-y-1 pl-5 text-xs">
              {sources.map((r) => (
                <li key={r.id}><b>{r.description}:</b> expected {r.expected.detail || r.expected.tag}; true {r.true.detail || r.true.tag}.</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="mb-1 font-medium">Still open</p>
            <ul className="list-disc space-y-1 pl-5 text-xs">
              {(basis?.open_questions || []).map((x, i) => <li key={i}>{x}</li>)}
              {!basis?.open_questions?.length && <li>Nothing recorded.</li>}
            </ul>
            {(basis?.assumptions || []).length > 0 && (
              <>
                <p className="mb-1 mt-3 font-medium">Assumptions</p>
                <ul className="list-disc space-y-1 pl-5 text-xs">{basis.assumptions.map((x, i) => <li key={i}>{x}</li>)}</ul>
              </>
            )}
          </div>
        </div>
      </div>
    </details>
  );
}
